import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { chatData } from "../../data/chatFlow";

function FlowMessage({ msg }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: msg.sender === "user" ? 20 : -20, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] sm:max-w-[85%] px-3 md:px-5 py-2 md:py-3.5 text-[11px] md:text-[13px] leading-relaxed shadow-xl break-words whitespace-pre-wrap font-medium tracking-tight ${
          msg.sender === "user" ?
            "bg-[#00BCF0] text-[#0B0F19] rounded-xl md:rounded-2xl rounded-tr-none italic font-black shadow-[0_5px_15px_rgba(0,188,240,0.2)]"
          : "bg-white/5 backdrop-blur-md text-slate-200 border border-white/10 rounded-xl md:rounded-2xl rounded-tl-none"
        }`}>
        {msg.text}
      </div>
    </motion.div>
  );
}

export function GuideTab({
  t,
  flowHistory,
  isTyping,
  nodeKey,
  lang,
  onOptionClick,
}) {
  const scrollRef = useRef(null);

  // Smooth scrolls to bottom on new messages OR when typing indicator toggles
  useEffect(() => {
    if (!scrollRef.current) return;

    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [flowHistory, isTyping]);

  return (
    <div className="flex flex-col h-full">
      {/* Main Chat Feed */}
      <div
        ref={scrollRef}
        className="
          flex-grow
          min-h-0
          overflow-y-scroll
          scroll-smooth
          space-y-3
          md:space-y-5
          pb-6
          md:pb-10
          pr-1
          /* Hides scrollbars across Chrome, Safari, Firefox, and Edge */
          [&::-webkit-scrollbar]:hidden
          [-ms-overflow-style:none]
          [scrollbar-width:none]
        ">
        {flowHistory.map((msg, i) => (
          <FlowMessage key={`f-${i}`} msg={msg} />
        ))}

        {isTyping && (
          <div className="flex gap-1 md:gap-1.5 p-3 md:p-4 bg-white/5 border border-white/5 w-fit rounded-2xl rounded-tl-none backdrop-blur-sm">
            {[0, 1, 2].map((d) => (
              <motion.span
                key={d}
                animate={{ y: [0, -5, 0], opacity: [0.3, 1, 0.3] }}
                transition={{
                  repeat: Infinity,
                  duration: 0.8,
                  delay: d * 0.1,
                }}
                className="w-1 h-1 md:w-1.5 md:h-1.5 bg-[#00BCF0] rounded-full"
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer / Choices Area */}
      <div className="flex-none p-3 md:p-5 bg-transparent border-t border-white/5">
        <div className="flex items-center gap-2 mb-2 md:mb-3">
          <span className="w-3 md:w-4 h-[1px] bg-[#EAB308]" />
          <p className="text-[9px] md:text-[10px] font-black text-[#EAB308] uppercase tracking-[0.2em]">
            {t.strategicChoices}
          </p>
        </div>

        <div className="flex flex-wrap gap-1.5 md:gap-2 max-h-[100px] md:max-h-[120px] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {chatData[lang]?.[nodeKey]?.options?.map((opt, i) => (
            <motion.button
              key={`${nodeKey}-${i}`}
              type="button"
              whileHover={{
                scale: 1.02,
                backgroundColor: "rgba(255,255,255,0.1)",
                borderColor: "#00BCF0",
              }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onOptionClick(opt.label, opt.next)}
              className="text-[10px] md:text-[10px] font-black uppercase italic border border-[#00BCF0]/30 text-[#00BCF0] px-2 md:px-3 py-1.5 md:py-2 rounded-lg md:rounded-xl transition-all tracking-tight">
              {opt.label}
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
}
